import { MfaError } from '@bco/ui';

/**
 * Pure helpers for the Login MFA challenge step.
 *
 * Extracted from the LoginPage component so they can be unit-tested without
 * rendering the page (which pulls in Vite-only `import.meta.env` and the
 * Module-Federation `federationInit` graph). The component composes these.
 */

/** Format remaining seconds as `m:ss` for the challenge countdown. */
export function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** How the challenge step should react to a failed respond-challenge attempt. */
export type ChallengeErrorOutcome =
  | {
      kind: 'invalid';
      /** Translation key for the alert message. */
      messageKey:
        | 'auth.mfa.error.codeInvalid'
        | 'auth.mfa.error.backupCodeInvalid';
      color: 'red';
      clearInput: true;
      refocus: true;
    }
  | {
      kind: 'session-expired';
      /** Caller returns to the credentials step. */
    }
  | {
      kind: 'too-many';
      messageKey: 'auth.mfa.error.tooManyAttempts';
      color: 'red';
      lockInput: true;
    }
  | {
      kind: 'network';
      messageKey: 'errors.network';
      color: 'red';
    };

/**
 * Classify a respond-challenge failure into the UI outcome.
 *
 * @param error  the thrown value (an {@link MfaError} for typed failures).
 * @param useBackup  whether the user is entering a backup code (affects the
 *   invalid-code message).
 */
export function classifyChallengeError(
  error: unknown,
  useBackup: boolean
): ChallengeErrorOutcome {
  if (error instanceof MfaError) {
    switch (error.code) {
      case 'MFA_CODE_INVALID':
        return {
          kind: 'invalid',
          messageKey: useBackup
            ? 'auth.mfa.error.backupCodeInvalid'
            : 'auth.mfa.error.codeInvalid',
          color: 'red',
          clearInput: true,
          refocus: true,
        };
      case 'MFA_SESSION_EXPIRED':
        return { kind: 'session-expired' };
      case 'MFA_TOO_MANY_ATTEMPTS':
        return {
          kind: 'too-many',
          messageKey: 'auth.mfa.error.tooManyAttempts',
          color: 'red',
          lockInput: true,
        };
    }
  }
  return { kind: 'network', messageKey: 'errors.network', color: 'red' };
}
